import React from 'react';
import { Link } from 'react-router-dom';
import './index.scss';

interface Section {
  title: string;
  points: string[];
}

interface LegalPageProps {
  title: string;
  updated: string;
  intro: string;
  sections: Section[];
}

const LegalPage: React.FC<LegalPageProps> = ({
  title,
  updated,
  intro,
  sections,
}) => (
  <div className="legal__page">
    <div className="legal__card">
      <Link to="/sign-up" className="legal__back">
        &larr; Back to sign up
      </Link>
      <h1>{title}</h1>
      <p className="legal__updated">Last updated: {updated}</p>
      <p>{intro}</p>
      {sections.map((section, index) => (
        <section key={section.title}>
          <h2>
            {index + 1}. {section.title}
          </h2>
          <ul>
            {section.points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  </div>
);

export const TermsOfServiceIndex: React.FC = () => (
  <LegalPage
    title="Terms of Service"
    updated="21 October 2026"
    intro="These terms govern your use of Gold Pulse. By creating an account you agree to them."
    sections={[
      {
        title: 'Use of the service',
        points: [
          'Gold Pulse is a tool to manage gold loans, pledges, interest, bank pledges, recovery and reports for your organization.',
          'You must provide accurate information when you sign up and keep it up to date.',
          'You are responsible for all activity under your organization account and for the users you create.',
        ],
      },
      {
        title: 'Account security',
        points: [
          'Keep your username and password confidential and do not share them.',
          'Usernames are not case sensitive.',
          'Tell us immediately if you suspect unauthorized access to your account.',
        ],
      },
      {
        title: 'Your data',
        points: [
          'You own the data you enter, including customer, loan and transaction records.',
          'You are responsible for having the right to store and process your customers’ information.',
          'You are responsible for the accuracy of the records and calculations you rely on.',
        ],
      },
      {
        title: 'Acceptable use',
        points: [
          'Do not use the service for any unlawful purpose.',
          'Do not attempt to break, probe or overload the service or access data that is not yours.',
        ],
      },
      {
        title: 'Organization code',
        points: [
          'The organization code chosen at sign up is used in agent and bank account numbers and cannot be changed later.',
        ],
      },
      {
        title: 'Availability and liability',
        points: [
          'We work to keep the service available but do not guarantee it will be uninterrupted or error free.',
          'To the extent permitted by law, we are not liable for indirect or consequential losses arising from use of the service.',
        ],
      },
      {
        title: 'Changes and termination',
        points: [
          'We may update these terms; continued use after an update means you accept it.',
          'We may suspend accounts that breach these terms.',
        ],
      },
    ]}
  />
);

export const PrivacyPolicyIndex: React.FC = () => (
  <LegalPage
    title="Privacy Policy"
    updated="21 October 2026"
    intro="This policy explains what information Gold Pulse collects and how it is used."
    sections={[
      {
        title: 'Information we collect',
        points: [
          'Account details: name, username, email address, phone number and password (stored securely, never in plain text).',
          'Organization details: organization name, code and logo.',
          'Business records you enter: customers, loans, pledges, transactions and reports.',
        ],
      },
      {
        title: 'How we use it',
        points: [
          'To create and secure your account, including verifying your email with a one-time code.',
          'To provide the features of the service and send service-related emails such as OTPs and ledger emails.',
          'To maintain, improve and protect the service.',
        ],
      },
      {
        title: 'Sharing',
        points: [
          'We do not sell your personal information.',
          'We share data only with service providers that help us run the service, or when required by law.',
        ],
      },
      {
        title: 'Security and retention',
        points: [
          'We use reasonable technical measures to protect your data.',
          'We keep data while your account is active and as needed to meet legal obligations.',
        ],
      },
      {
        title: 'Your choices',
        points: [
          'You can ask us to correct or delete your account information.',
          'You can reset your password at any time from the forgot password page.',
        ],
      },
      {
        title: 'Changes',
        points: [
          'We may update this policy; the date above shows when it last changed.',
        ],
      },
    ]}
  />
);
